import { Fragment } from 'react';
import type { ReactNode } from 'react';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { ISectionWithFields } from '@/features/module-builder/types';
import type { ResolvedLayout, ResolvedRegion } from '@/lib/record-form/layout';

export interface RecordLayoutRendererProps {
    layout: ResolvedLayout;
    /** Renders one section (form grid, read-only pane, …) — caller decides. */
    renderSection: (section: ISectionWithFields) => ReactNode;
    /** Rendered above the arranged regions/tabs (e.g. header field strip). */
    header?: ReactNode;
}

function SectionStack({
    sections,
    renderSection,
}: {
    sections: ISectionWithFields[];
    renderSection: (section: ISectionWithFields) => ReactNode;
}) {
    return (
        <>
            {sections.map((section) => (
                <Fragment key={section.id}>{renderSection(section)}</Fragment>
            ))}
        </>
    );
}

function findRegion(regions: ResolvedRegion[], key: string): ResolvedRegion | undefined {
    return regions.find((region) => region.key === key);
}

/**
 * Template-aware arrangement of a resolved module layout. Delegates section
 * rendering to the caller so the same shell serves edit forms and detail views.
 */
export function RecordLayoutRenderer({ layout, renderSection, header }: RecordLayoutRendererProps) {
    let body: ReactNode = null;

    if (layout.template === 'tabbed') {
        const firstTab = layout.tabs[0];
        body =
            layout.tabs.length === 0 || !firstTab ? null : (
                <Tabs defaultValue={firstTab.id}>
                    <TabsList variant="line" className="w-full justify-start">
                        {layout.tabs.map((tab) => (
                            <TabsTrigger key={tab.id} value={tab.id} className="flex-none">
                                {tab.label}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                    {layout.tabs.map((tab) => (
                        <TabsContent key={tab.id} value={tab.id} className="flex flex-col gap-6">
                            <SectionStack sections={tab.sections} renderSection={renderSection} />
                        </TabsContent>
                    ))}
                </Tabs>
            );
    } else if (layout.template === 'two_column') {
        const main = findRegion(layout.regions, 'main') ?? layout.regions[0];
        const sidebar = findRegion(layout.regions, 'sidebar');
        body = (
            <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-3">
                <div className="flex min-w-0 flex-col gap-6 lg:col-span-2">
                    <SectionStack sections={main?.sections ?? []} renderSection={renderSection} />
                </div>
                <div className="flex min-w-0 flex-col gap-6">
                    <SectionStack
                        sections={sidebar?.sections ?? []}
                        renderSection={renderSection}
                    />
                </div>
            </div>
        );
    } else if (layout.template === 'three_column') {
        body = (
            <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-3">
                {layout.regions.map((region) => (
                    <div key={region.key} className="flex min-w-0 flex-col gap-6">
                        <SectionStack sections={region.sections} renderSection={renderSection} />
                    </div>
                ))}
            </div>
        );
    } else {
        // 'full' — stacked sections across all regions, in order.
        body = (
            <div className="flex flex-col gap-6">
                <SectionStack
                    sections={layout.regions.flatMap((region) => region.sections)}
                    renderSection={renderSection}
                />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-6">
            {header}
            {body}
        </div>
    );
}
